import React, {useEffect, useState} from 'react';
import {Button, Spinner} from '@vkontakte/vkui';
import {
    Icon24CupOutline,
    Icon28ChevronLeftOutline,
    Icon28ChevronRightOutline,
    Icon28CupOutline,
    Icon28Users3Outline,
    Icon36CoinsStacks2Outline
} from '@vkontakte/icons';
import {getTournamentHistory} from '../../services/tournament';
import BackgroundLetters from "../../components/Background/Background";

// Кэш истории турниров по страницам, чтобы избегать повторных запросов.
const tournamentHistoryCache = new Map();

export function TournamentHistory() {
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const isEmptyTournaments = tournaments.length === 0;

    useEffect(() => {
        loadTournamentHistory(currentPage);
    }, [currentPage]);

    // loadTournamentHistory подтягивает историю турниров и сохраняет страницу в кэше.
    const loadTournamentHistory = (page) => {
        const cachedPage = tournamentHistoryCache.get(page);
        // Пустые страницы не кэшируем навсегда, чтобы не залипать на "нет турниров".
        if (cachedPage && cachedPage.tournaments.length > 0) {
            setTournaments(cachedPage.tournaments);
            setTotalPages(cachedPage.totalPages);
            setHasNext(cachedPage.hasNext);
            setLoading(false);
            return;
        }

        setLoading(true);
        getTournamentHistory(page, 9).then(resp => {
            // Защищаемся от изменений формы ответа и пустых payload'ов.
            const payload = resp?.data?.response ?? resp?.data ?? {};
            const tournamentsList = Array.isArray(payload.tournaments)
                ? payload.tournaments
                : Array.isArray(payload.items)
                    ? payload.items
                    : [];
            const total = Number.isFinite(payload.total) ? payload.total : tournamentsList.length;
            const limit = Number.isFinite(payload.limit) && payload.limit > 0 ? payload.limit : tournamentsList.length || 1;
            const pageCount = Math.max(1, Math.ceil(total / limit));
            setTournaments(tournamentsList);
            setTotalPages(pageCount);
            setHasNext(Boolean(payload.has_next) || page < pageCount);
            tournamentHistoryCache.set(page, {
                tournaments: tournamentsList,
                totalPages: pageCount,
                hasNext: Boolean(payload.has_next) || page < pageCount
            });
        }).catch(err => {
            console.error('Failed to load tournament history:', err);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (hasNext) {
            setCurrentPage(currentPage + 1);
        }
    };

    const getTournamentTitle = (type) => {
        switch (type) {
            case 'daily':
                return 'Daily tournament';
            case 'weekly':
                return 'Weekly tournament';
            case 'monthly':
                return 'Monthly tournament';
            default:
                return 'Tournament';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) {
            return '—';
        }
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return '—';
        }
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <>
            <BackgroundLetters/>

            {/* Глобальный контейнер с ограничением ширины, как в других панелях */}
            {/* Контент поверх фоновых букв */}
            <div className={"relative z-10 flex flex-col p-4 items-center mx-auto w-full max-w-lg"}>
                {/* Ожидаем загрузку данных */}
                {loading === true && <Spinner size={"large"}/>}

                {/* Отображаем контент */}
                {!loading && <>
                    <div className={"flex w-full flex-col"}>
                        <div className={"w-full bg-gray-800 text-gray-400 rounded-2xl shadow-md p-5 space-y-6"}>
                            {/* Шапка истории */}
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-12 h-12 flex-shrink-0 rounded-xl bg-yellow-400 text-yellow-800 flex items-center justify-center">
                                    <Icon28CupOutline/>
                                </div>
                                <div className={"text-gray-400"}>
                                    <p className="text-base font-semibold">Tournament history</p>
                                    <p className="text-sm">Results and winners of completed tournaments</p>
                                </div>
                            </div>

                            <div className={"flex flex-col gap-3"}>
                                {isEmptyTournaments ? (
                                    <div
                                        className="w-full bg-gray-700 text-gray-400 rounded-xl p-4 shadow-sm border border-gray-800 text-center">
                                        {/* Плашка пустого состояния */}
                                        <p className="text-sm text-gray-300">No completed tournaments yet</p>
                                        <p className="text-xs text-gray-500 mt-1">Results will appear after tournaments finish</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {tournaments.map((tournament) => (
                                            <TournamentHistoryCard
                                                key={tournament.id}
                                                tournament={tournament}
                                                title={getTournamentTitle(tournament.tournament_type)}
                                                formatDate={formatDate}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Пагинация */}
                                {totalPages > 1 && (
                                    <div className="flex justify-between items-center pt-2">
                                        <Button
                                            size="s"
                                            mode="secondary"
                                            onClick={handlePrevPage}
                                            disabled={currentPage === 1}
                                            before={<Icon28ChevronLeftOutline width={20} height={20}/>}
                                            className="text-gray-400"
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            size="s"
                                            mode="secondary"
                                            onClick={handleNextPage}
                                            disabled={!hasNext}
                                            after={<Icon28ChevronRightOutline width={20} height={20}/>}
                                            className="text-gray-400"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>}
            </div>
        </>
    );
}

// TournamentHistoryCard - карточка завершенного турнира
function TournamentHistoryCard({tournament, title, formatDate}) {
    const getTypeColor = (type) => {
        switch (type) {
            case 'daily':
                return 'bg-blue-100 text-blue-800';
            case 'weekly':
                return 'bg-green-100 text-green-800';
            case 'monthly':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="bg-gray-700 text-gray-400 rounded-xl p-4 shadow-sm border border-gray-800">
            {/* Заголовок турнира */}
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <Icon24CupOutline className="text-yellow-500" width={20} height={20}/>
                    <span className="font-semibold text-sm text-gray-100">{title}</span>
                </div>
            </div>

            {/* Информация о турнире */}
            <div className="flex justify-between items-center mb-3 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                    <Icon28Users3Outline width={16} height={16}/>
                    <span>Players: {tournament.participants_count}</span>
                </div>
                <div className="flex items-center gap-1">
                    <span>Finished: {formatDate(tournament.end_at)}</span>
                </div>
            </div>

            {/* Призы */}
            <div className="mb-3">
                <span className="text-xs text-gray-300 font-bold mb-1">Prizes:</span>
                <div className={"flex flex-col md:flex-row md:gap-4"}>
                    {tournament.reward?.places?.map((place, index) => (
                        <div key={index} className="flex items-center flex-row text-xs text-gray-400 mb-1">
                            <span className="mr-1">{index + 1} place:</span>
                            <span className="text-yellow-600 mr-1">{place.coins}</span>
                            <Icon36CoinsStacks2Outline width={14} height={14} className="text-yellow-500"/>
                        </div>
                    ))}
                </div>
            </div>

            {/* Победители */}
            {tournament.leaders && tournament.leaders.length > 0 && (
                <div className="mb-3">
                    <span className="text-xs text-gray-300 font-bold mb-2 block">Winners:</span>
                    <div className="space-y-1">
                        {tournament.leaders.map((leader, index) => (
                            <div key={leader.user_id}
                                 className="flex items-center justify-between text-xs text-gray-400">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="font-semibold text-gray-200">{index + 1}.</span>
                                    <img
                                        src={leader.player_avatar}
                                        alt={leader.player_name}
                                        className="w-5 h-5 rounded-full"
                                    />
                                    <span className="text-gray-200 truncate max-w-24">{leader.player_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="text-gray-200 font-medium">{leader.score} {leader.score === 1 ? 'point' : 'points'}</span>
                                    {tournament.reward?.places?.[index] && (
                                        <div className="flex items-center gap-1">
                                            <span
                                                className="text-yellow-600 text-xs">+{tournament.reward.places[index].coins}</span>
                                            <Icon36CoinsStacks2Outline width={12} height={12}
                                                                       className="text-yellow-500"/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
